import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  Injector,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, debounce, EMPTY, firstValueFrom, Subject, timer } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  AdminUserListItem,
  AdminUserListQueryOptions,
  UserAdminService,
} from '@nebalkon-workspace/admin-api';
import { LocalStorageApiService } from '@nebalkon-workspace/browser-api';
import {
  clientMessengersFromUser,
  ClientMessengers,
  formatWalletAmount,
} from '@nebalkon-workspace/cabinet-core';
import {
  hasStaffRole,
  mapBackendRoleStringsToUserRoles,
} from '@nebalkon-workspace/auth';
import { NotInput } from '@nebalkon-workspace/ui-kit';
import { bindStaffPageTitleCount } from './staff-page-title-count.util';
import { bindStaffPageHeaderAction } from './staff-page-header-action.util';
import { StaffWalletStatsService } from '../wallet/staff-wallet-stats.service';
import { StaffClientMessengerActionsComponent } from './staff-client-messenger-actions.component';
import {
  parseStaffUsersListFilterQueryValue,
  resolveStaffUsersListFilters,
  saveStaffUsersListFilters,
  type StaffUsersListFilters,
} from './staff-users-list-filters.storage';

const PAGE_SIZE = 20;
const NEAR_BOTTOM_PX = 200;
const MAX_FILL_VIEWPORT_PAGES = 5;

@Component({
  selector: 'lib-staff-users-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NotInput, FormsModule, StaffClientMessengerActionsComponent],
  templateUrl: './staff-users-list.component.html',
  styleUrl: './staff-users-list.component.scss',
})
export class StaffUsersListComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  protected readonly staffCabinetPath = this.#cabinetPaths.staff;
  readonly #userService = inject(UserAdminService);
  readonly #walletStats = inject(StaffWalletStatsService);
  readonly #localStorage = inject(LocalStorageApiService);
  readonly #injector = inject(Injector);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly usersListEl = viewChild<ElementRef<HTMLElement>>('usersList');
  protected readonly headerAddActionTpl = viewChild<TemplateRef<unknown>>('headerAddAction');

  protected readonly searchQuery = signal('');
  protected readonly filters = signal<StaffUsersListFilters>(
    resolveStaffUsersListFilters(
      this.#route.snapshot.queryParamMap,
      this.#localStorage,
    ),
  );
  protected readonly loading = signal(true);
  protected readonly loadingMore = signal(false);
  protected readonly initialized = signal(false);
  protected readonly loadError = signal(false);
  protected readonly items = signal<AdminUserListItem[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly activeTotalCount = signal(0);

  protected readonly displayCount = computed(() =>
    this.initialized() ? this.activeTotalCount() : null,
  );

  protected readonly hasMore = computed(
    () => this.items().length < this.totalCount(),
  );

  constructor() {
    bindStaffPageTitleCount(this.embeddedInCabinetPage, this.displayCount);
    bindStaffPageHeaderAction(
      this.embeddedInCabinetPage,
      this.headerAddActionTpl,
      this.initialized,
    );
  }

  private readonly searchSubject = new Subject<string>();
  private lastSearch = '';
  private scrollListenerBound = false;
  private listScrollRoot?: HTMLElement;
  private scrollRaf = 0;
  private fillViewportPages = 0;

  public ngOnInit(): void {
    this.#walletStats.refresh();

    this.#route.queryParamMap
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((qm) => {
        if (!qm.has('debtors_only')) {
          return;
        }
        const next = parseStaffUsersListFilterQueryValue(qm.get('debtors_only'));
        if (next === this.filters().debtorsOnly) {
          return;
        }
        this.#applyFilters({ debtorsOnly: next }, { syncDebtorsUrl: false });
      });

    this.#destroyRef.onDestroy(() => {
      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
      }
      if (this.listScrollRoot) {
        this.listScrollRoot.removeEventListener('scroll', this.onListScroll);
      }
    });

    this.searchSubject.pipe(
      debounce((value) => (value ? timer(500) : timer(0))),
    ).subscribe((search) => {
      void this.reloadFirstPage(search);
    });

    this.searchSubject.next('');
  }

  protected onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  protected onFilterChange(partial: Partial<StaffUsersListFilters>): void {
    this.#applyFilters(partial, {
      syncDebtorsUrl: 'debtorsOnly' in partial,
    });
  }

  protected isDebtor(item: AdminUserListItem): boolean {
    return (item.wallet_balance_bynd ?? 0) < 0;
  }

  protected formatDebtorBalance(item: AdminUserListItem): string {
    const balance = item.wallet_balance_bynd;
    if (balance == null || balance >= 0) {
      return '';
    }
    return `${formatWalletAmount(balance)} BYN`;
  }

  #applyFilters(
    partial: Partial<StaffUsersListFilters>,
    options: { syncDebtorsUrl: boolean },
  ): void {
    const current = this.filters();
    const next: StaffUsersListFilters = { ...current, ...partial };

    if (
      Object.keys(partial).every(
        (key) =>
          current[key as keyof StaffUsersListFilters] ===
          next[key as keyof StaffUsersListFilters],
      )
    ) {
      return;
    }

    this.filters.set(next);
    saveStaffUsersListFilters(this.#localStorage, next);

    if (options.syncDebtorsUrl) {
      void this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { debtors_only: next.debtorsOnly ? '1' : null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    if (this.initialized()) {
      void this.reloadFirstPage(this.lastSearch);
    }
  }

  private buildUsersQueryOptions(offset = 0): AdminUserListQueryOptions {
    const f = this.filters();

    return {
      limit: PAGE_SIZE,
      offset,
      ...(f.excludeStaff ? { excludeStaff: true } : {}),
      ...(f.debtorsOnly ? { debtorsOnly: true } : {}),
      ...(f.verifiedOnly ? { verifiedOnly: true } : {}),
      ...(f.activeStorageOnly ? { activeStorageOnly: true } : {}),
      ...(f.activeOrdersOnly ? { activeOrdersOnly: true } : {}),
      ...(f.blockedOnly ? { blockedOnly: true } : {}),
      ...(f.withoutStorageOnly ? { withoutStorageOnly: true } : {}),
      ...(f.partnersOnly ? { partnersOnly: true } : {}),
      ...(f.personalRateOnly ? { personalRateOnly: true } : {}),
    };
  }

  private async reloadFirstPage(search: string): Promise<void> {
    this.loading.set(true);
    this.loadError.set(false);
    this.lastSearch = search;
    this.items.set([]);
    this.fillViewportPages = 0;
    try {
      const res = await firstValueFrom(
        this.#userService.getUsers(search, this.buildUsersQueryOptions()),
      );
      this.items.set(res.data);
      this.totalCount.set(res.total);
      this.activeTotalCount.set(res.active_total);
      this.initialized.set(true);
      afterNextRender(
        () => {
          this.ensureScrollListener();
          this.scheduleCheckNearBottom();
        },
        { injector: this.#injector },
      );
    } catch {
      this.initialized.set(true);
      this.loadError.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private ensureScrollListener(): void {
    const root = this.usersListEl()?.nativeElement;
    if (!root || this.scrollListenerBound) {
      return;
    }
    this.scrollListenerBound = true;
    this.listScrollRoot = root;
    root.addEventListener('scroll', this.onListScroll, { passive: true });
  }

  private readonly onListScroll = (): void => {
    this.scheduleCheckNearBottom();
  };

  private scheduleCheckNearBottom(): void {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.checkNearBottomOrFillViewport();
    });
  }

  private checkNearBottomOrFillViewport(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }
    const root = this.usersListEl()?.nativeElement;
    if (!root) {
      return;
    }

    const scrollHeight = root.scrollHeight;
    const clientHeight = root.clientHeight;
    const scrollTop = root.scrollTop;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    const contentDoesNotScroll =
      scrollHeight <= clientHeight + 4 && this.hasMore();

    if (contentDoesNotScroll && this.fillViewportPages < MAX_FILL_VIEWPORT_PAGES) {
      this.fillViewportPages++;
      this.fetchNextPage();
      return;
    }

    if (distanceFromBottom <= NEAR_BOTTOM_PX) {
      this.fetchNextPage();
    }
  }

  private fetchNextPage(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }
    const offset = this.items().length;
    this.loadingMore.set(true);
    this.#userService
      .getUsers(this.lastSearch, this.buildUsersQueryOptions(offset))
      .pipe(
        catchError(() => {
          this.loadingMore.set(false);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (res) => {
          this.items.update((prev) => [...prev, ...res.data]);
          this.totalCount.set(res.total);
          this.activeTotalCount.set(res.active_total);
          this.loadingMore.set(false);
          afterNextRender(
            () => {
              this.checkNearBottomOrFillViewport();
            },
            { injector: this.#injector },
          );
        },
      });
  }

  protected formatName(item: AdminUserListItem): string {
    const ind = item.individual;
    const lastName = ind.last_name
      ? ind.last_name.charAt(0).toUpperCase() + ind.last_name.slice(1)
      : '';
    const parts = [lastName, ind.first_name, ind.patronymic].filter(Boolean);

    return parts.join(' ') || item.user.phone;
  }

  protected isStaffUser(roles: string[] | undefined): boolean {
    if (!roles?.length) {
      return false;
    }
    return hasStaffRole(mapBackendRoleStringsToUserRoles(roles));
  }

  protected clientMessengers(row: AdminUserListItem): ClientMessengers {
    return clientMessengersFromUser(row.user);
  }

  protected onAdd(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'users', 'new']);
  }

  protected userDetailHref(userId: string): string {
    return `${this.staffCabinetPath}/users/${encodeURIComponent(userId)}`;
  }

  protected openUser(userId: string, event: MouseEvent): void {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    void this.#router.navigate([this.#cabinetPaths.staff, 'users', userId]);
  }
}
